import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FILTER_OPTIONS } from "@/lib/constants";
import { FilterX, SlidersHorizontal, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PRIMARY = [
  ["geography", "Geography"], ["country", "Country"], ["specialty", "HCP Specialty"],
  ["segment", "HCP Segment"], ["setting", "Setting"], ["theme_id", "Clinical Theme"], ["treatment_line", "Treatment Line"],
];

const ADVANCED = [
  ["patient_subgroup", "Patient Subgroup"], ["biomarker", "Biomarker"], ["competitor", "Competitor"],
  ["evidence_type", "Evidence Type"], ["source_type", "Source Type"], ["congress", "Congress"],
  ["confidence", "Confidence"], ["review_status", "Review Status"], ["position", "Position"], ["access_class", "Source Class"],
];

const OPTION_KEY = { theme_id: "theme" };

export const FilterBar = ({ filters, setFilters }) => {
  const [options, setOptions] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [more, setMore] = useState(false);

  const loadOptions = () => {
    setLoadError(false);
    api.get("/filters/options").then((r) => setOptions(r.data)).catch(() => setLoadError(true));
  };
  useEffect(loadOptions, []);

  const activeCount = Object.values(filters).filter((v) => v && v !== "all").length;

  const getOptions = (key) => {
    if (key === "position" || key === "access_class") return FILTER_OPTIONS[key];
    const opts = options?.[OPTION_KEY[key] || key] || [];
    return opts;
  };

  const renderSelect = ([key, label]) => {
    const opts = getOptions(key);
    return (
      <Select key={key} value={filters[key] || "all"} onValueChange={(v) => setFilters({ ...filters, [key]: v })}>
        <SelectTrigger data-testid={`filter-${key}`} aria-label={`Filter by ${label}`}
          className="w-auto h-8 text-xs gap-1.5 rounded-full border-slate-200 bg-slate-50 hover:border-teal-400 focus:ring-teal-500">
          <span className="text-slate-400">{label}:</span>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All</SelectItem>
          {opts.map((o) => (typeof o === "object"
            ? <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
            : <SelectItem key={o} value={o}>{o}</SelectItem>))}
        </SelectContent>
      </Select>
    );
  };

  return (
    <div className="bg-white border border-slate-200/80 rounded-xl p-3 shadow-sm" data-testid="filter-bar" role="group" aria-label="Intelligence filters">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1.5 text-slate-500 pr-2 border-r border-slate-200">
          <SlidersHorizontal className="w-4 h-4" aria-hidden="true" />
          <span className="font-mono text-[10px] uppercase tracking-widest">Filters</span>
        </div>
        {!options && !loadError && (
          <span className="flex items-center gap-1.5 text-xs text-slate-400" role="status" aria-live="polite" data-testid="filter-options-loading">
            <Loader2 className="w-3.5 h-3.5 animate-spin" aria-hidden="true" />Loading filter options…
          </span>
        )}
        {loadError && (
          <span className="flex items-center gap-2 text-xs text-red-600" role="alert" data-testid="filter-options-error">
            Filter options could not be loaded.
            <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={loadOptions} data-testid="filter-options-retry-btn">Retry</Button>
          </span>
        )}
        {PRIMARY.map(renderSelect)}
        <button onClick={() => setMore(!more)} data-testid="more-filters-btn"
          aria-expanded={more} aria-controls="advanced-filters-panel"
          aria-label={more ? "Show fewer filters" : "Show more filters"}
          className="flex items-center gap-1 h-8 px-3 text-xs font-medium text-teal-700 bg-teal-50 border border-teal-200 rounded-full hover:bg-teal-100">
          {more ? <ChevronUp className="w-3.5 h-3.5" aria-hidden="true" /> : <ChevronDown className="w-3.5 h-3.5" aria-hidden="true" />}
          {more ? "Fewer filters" : "More filters"}
        </button>
        {activeCount > 0 && (
          <Button data-testid="clear-filters-btn" variant="ghost" size="sm"
            aria-label={`Clear ${activeCount} active filter${activeCount > 1 ? "s" : ""}`}
            className="h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => { setFilters({}); toast.info("Filters cleared"); }}>
            <FilterX className="w-3.5 h-3.5 mr-1" aria-hidden="true" /> Clear ({activeCount})
          </Button>
        )}
      </div>
      {more && (
        <div id="advanced-filters-panel" className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-slate-100" data-testid="advanced-filters">
          {ADVANCED.map(renderSelect)}
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-slate-400">Date:</span>
            <Input type="date" value={filters.date_from || ""} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              aria-label="Filter from date"
              className="h-8 w-[135px] text-xs rounded-full bg-slate-50 border-slate-200" data-testid="filter-date-from" />
            <span className="text-xs text-slate-400" aria-hidden="true">→</span>
            <Input type="date" value={filters.date_to || ""} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              aria-label="Filter to date"
              className="h-8 w-[135px] text-xs rounded-full bg-slate-50 border-slate-200" data-testid="filter-date-to" />
          </div>
        </div>
      )}
    </div>
  );
};
